import { CziConversionStatus, FileDatasetValidationStatus, FileModel, UploadFileItem } from '@ca/core/models';
import { FileSegmentationValidationStatus } from 'src/app/core/types/file-segmentation-validation-statuses.type';
import { CZI_EXTENSION, FILE_TYPES_SUPPORTED_BY_APEER_MULTI_DOTS } from '../../app/shared/constants/constants';
import * as UrlUtils from '../url/url-utils';
import { diacritics } from './file-accent-letters.data';

export function getFileNameWithExtension(fileNameOrUrl?: string): string {
    let fileName = '';
    if (fileNameOrUrl) {
        if (UrlUtils.isUrl(fileNameOrUrl)) {
            fileNameOrUrl = decodeURIComponent(fileNameOrUrl);
            const urlWithoutQueryParameters = UrlUtils.removeQueryParameter(fileNameOrUrl);
            const fileNameWithPossibleExtension = urlWithoutQueryParameters.substring(
                urlWithoutQueryParameters.lastIndexOf('/') + 1,
                urlWithoutQueryParameters.length,
            );
            fileName = fileNameWithPossibleExtension;
        } else {
            fileName = fileNameOrUrl;
        }
    }

    return fileName;
}

export function getFileNameWithoutExtension(fileNameOrUrl?: string): string {
    return trimFileExtension(getFileNameWithExtension(fileNameOrUrl));
}

/**
 * Pass a URL or file name and get only the type extension
 *
 * For example:
 * 'https://www.google.de/path/to/image/image.jpg?param=val1&param=val2' => 'jpg'
 *
 * @param nameOrUrl - URL to extract the path from
 * @return fileExtension - returns the file extension from the URL
 *
 */
export function getFileExtension(nameOrUrl: string): string | undefined {
    if (nameOrUrl == null || !nameOrUrl.includes('.')) {
        return undefined;
    }

    const urlParts = nameOrUrl.split('.');

    urlParts[urlParts.length - 1] = UrlUtils.removeQueryParameter(urlParts[urlParts.length - 1]);

    // filename with multi dots extension has at least 3 split parts ['', 'ome', 'tiff']
    if (urlParts.length >= 3) {
        const matchingMultiDotsExtensions = FILE_TYPES_SUPPORTED_BY_APEER_MULTI_DOTS.filter(
            (multiDotsFileType: string) => `${urlParts[urlParts.length - 2]}.${urlParts[urlParts.length - 1]}` === multiDotsFileType,
        );
        if (matchingMultiDotsExtensions.length === 1) {
            return matchingMultiDotsExtensions[0];
        }
    }

    const extension = urlParts[urlParts.length - 1];

    if (extension.includes('/') || extension === '') {
        return undefined;
    }
    return extension;
}

export function trimFileExtension(fileName: string): string {
    let fileNameWithoutExtension = fileName;

    const fileExtension = getFileExtension(fileNameWithoutExtension);
    if (fileExtension != null) {
        fileNameWithoutExtension = fileName.substring(0, fileName.lastIndexOf(`.${fileExtension}`));
    }
    return fileNameWithoutExtension;
}

export function isFileNameWithExtensionContainedInFiles(tokenOrFilename: string, files: FileModel[]): boolean {
    return files.some(file => {
        return getFileNameWithExtension(file.token) === getFileNameWithExtension(tokenOrFilename);
    });
}

export function isFileNameWithoutExtensionContainedInFiles(tokenOrFilename: string, files: FileModel[]): boolean {
    return files.some(file => {
        return getFileNameWithoutExtension(file.token) === getFileNameWithoutExtension(tokenOrFilename);
    });
}

export function isFileNameWithoutExtensionContainedInListOfFileNames(tokenOrFilename: string, fileNames: string[]): boolean {
    return fileNames.some(fileName => {
        return getFileNameWithoutExtension(fileName) === getFileNameWithoutExtension(tokenOrFilename);
    });
}

export function isFileNameContainedInFileNames(tokenOrFilename: string, fileNames: string[]): boolean {
    return fileNames.some(fileName => {
        return getFileNameWithExtension(fileName) === getFileNameWithExtension(tokenOrFilename);
    });
}

export function isFileNameWithExtensionContainedMoreThanOnceInFileNames(tokenOrFilename: string, fileNames: string[]): boolean {
    return fileNames.filter(fileName => getFileNameWithExtension(fileName) === getFileNameWithExtension(tokenOrFilename)).length > 1;
}

export function isFileNameWithoutExtensionContainedMoreThanOnceInFileNames(tokenOrFilename: string, fileNames: string[]): boolean {
    return fileNames.filter(fileName => getFileNameWithoutExtension(fileName) === getFileNameWithoutExtension(tokenOrFilename)).length > 1;
}

export function getFileUrlFromResult(value?: string | FileModel): string | undefined {
    let urlToCheck: string | undefined = undefined;
    if (value && typeof value === 'object') {
        if (value.token) {
            urlToCheck = value.token;
        }
    } else {
        urlToCheck = <string>value;
    }
    return urlToCheck;
}

export const ariApplicationsSupportedFileTypes: string[] = ['.zip', '.dcm'];

// Source: https://docs.openmicroscopy.org/bio-formats/6.0.1/supported-formats.html
// Source (list updated by ourselves):
export const machineLearningSupportedFileTypes: string[] = [
    '.1sc',
    '.2fl',
    '.acff',
    '.afi',
    '.afm',
    '.aim',
    '.al3d',
    '.ali',
    '.am',
    '.amiramesh',
    '.apl',
    '.arf',
    '.avi',
    '.bip',
    '.bmp',
    '.btf',
    '.c01',
    '.cfg',
    '.ch5',
    '.cif',
    '.cr2',
    '.crw',
    '.csv',
    '.cxd',
    '.czi',
    '.dat',
    '.dcm',
    '.dib',
    '.dicom',
    '.dm2',
    '.dm3',
    '.dm4',
    '.dti',
    '.dv',
    '.eps',
    '.epsi',
    '.exp',
    '.fdf',
    '.fff',
    '.ffr',
    '.fits',
    '.flex',
    '.fli',
    '.frm',
    '.gel',
    '.gif',
    '.grey',
    '.hdf',
    '.hdr',
    '.hed',
    '.his',
    '.htd',
    '.html',
    '.hx',
    '.i2i',
    '.ics',
    '.ids',
    '.im3',
    '.img',
    '.ims',
    '.inr',
    '.ipl',
    '.ipm',
    '.ipw',
    '.jp2',
    '.jpeg',
    '.jpg',
    '.jpk',
    '.jpx',
    '.klb',
    '.l2d',
    '.lei',
    '.lif',
    '.liff',
    '.lim',
    '.lms',
    '.lsm',
    '.map',
    '.mdb',
    '.mea',
    '.mnc',
    '.mng',
    '.mod',
    '.mrc',
    '.mrcs',
    '.mrw',
    '.msr',
    '.mtb',
    '.mvd2',
    '.naf',
    '.nd',
    '.nd2',
    '.ndpi',
    '.ndpis',
    '.nef',
    '.nhdr',
    '.nii.gz',
    '.nii',
    '.nrrd',
    '.obf',
    '.obsep',
    '.oib',
    '.oif',
    '.oir',
    '.ome.btf',
    '.ome.tf2',
    '.ome.tf8',
    '.ome.tif',
    '.ome.tiff',
    '.ome.xml',
    '.ome',
    '.par',
    '.pbm',
    '.pcoraw',
    '.pcx',
    '.pds',
    '.pgm',
    '.pic',
    '.pict',
    '.png',
    '.pnl',
    '.ppm',
    '.pr3',
    '.ps',
    '.psd',
    '.qptiff',
    '.r3d',
    '.raw',
    '.rec',
    '.res',
    '.scn',
    '.sdt',
    '.seq',
    '.sif',
    '.sld',
    '.sm2',
    '.sm3',
    '.spc',
    '.spe',
    '.spi',
    '.st',
    '.stk',
    '.stp',
    '.svs',
    '.sxm',
    '.tf2',
    '.tf8',
    '.tfr',
    '.tga',
    '.tif',
    '.tiff',
    '.tnb',
    '.top',
    '.v',
    '.vff',
    '.vms',
    '.vsi',
    '.vws',
    '.wat',
    '.wlz',
    '.wpi',
    '.xdce',
    '.xml',
    '.xqd',
    '.xqf',
    '.xv',
    '.xys',
    '.zfp',
    '.zfr',
];
export const supportedAnnotationTypesToUpload: string[] = ['.ome.tiff'];

// The list of allowed dataset file extension which contain multiple . characters.
const datasetSupportedTypesWithMultipleDots: string[] = ['.nii.gz', '.ome.tiff', '.ome.tif', '.ome.tf2', '.ome.tf8', '.ome.btf', '.ome.xml'];

/**
 * Characters that are not in the whitelist [A-Za-z0-9_()-] are considered invalid characters in file names.
 * @param value - File name.
 * @return boolean - Returns true if file name contains invalid character/s.
 *
 */
export function isInvalidCharacterContainedInFilename(value: string): boolean {
    const blacklistedCharacters = /[^a-zA-Z0-9-_()]+/gm;
    const extensionWithMultipleDots: string | undefined = datasetSupportedTypesWithMultipleDots.find(type => value.endsWith(type));
    let fileNameWithoutExtension: string = '';
    if (extensionWithMultipleDots != null) {
        fileNameWithoutExtension = value.substring(0, value.lastIndexOf(extensionWithMultipleDots));
    } else {
        fileNameWithoutExtension = value.substring(0, value.lastIndexOf('.'));
    }
    return blacklistedCharacters.test(fileNameWithoutExtension);
}

/**
 * Characters not included in regex pattern are invalid characters in file names. This method replaces them with _ character.
 * This function also replaces all Blob Storage invalid characters (,/\#%).
 * @param value - File name.
 * @return string - Returns file name where unsupported characters are replaced with _.
 *
 */
export function replaceFileNameInvalidCharacters(file: string): string {
    const blacklistedCharacters = /[^a-zA-Z0-9-_()]+/gm;
    const extensionWithMultipleDots: string | undefined = datasetSupportedTypesWithMultipleDots.find(type => file.endsWith(type));
    let fileNameWithoutExtension: string = '';
    let extension: string = '';

    if (extensionWithMultipleDots != null) {
        fileNameWithoutExtension = file.substring(0, file.lastIndexOf(extensionWithMultipleDots));
        extension = extensionWithMultipleDots;
    } else {
        fileNameWithoutExtension = file.substring(0, file.lastIndexOf('.'));
        extension = file.substring(file.lastIndexOf('.'), file.length);
    }
    fileNameWithoutExtension = fileNameWithoutExtension.replace(blacklistedCharacters, '_');
    return fileNameWithoutExtension + extension;
}

export function isSupportedType(fileName: string, supportedTypes: string[]): boolean {
    for (const supportedType of supportedTypes) {
        if (fileName.toLowerCase().endsWith(supportedType)) {
            return true;
        }
    }
    return false;
}

export function getFilenameFromBlobPath(blobPaths: string[]): string[] {
    return (Array.isArray(blobPaths) ? blobPaths.map((url: string) => url?.split('/')?.pop()).filter((f: string) => !!f) : []) as string[];
}

export function getNumberOfCziFiles(files: FileModel[]): number {
    return files.filter((file: FileModel) => getFileExtension(file.name)?.toLowerCase() === CZI_EXTENSION).length;
}

/**
 * Replaces accented letters based on accented letters data with alphabetic characters.
 * @param fileName name of the file provided by user
 * @returns Renamed filename (with replaced diacritics)
 */
export function replaceDiacriticsInFileName(fileName: string): string {
    const diacriticsBaseMap = {};
    // eslint-disable-next-line no-control-regex
    const umlautRegex = /[^\u0000-\u007E]/g;

    for (let i = 0; i < diacritics.length; i++) {
        const letters = diacritics[i].accentedLetters;
        for (let j = 0; j < letters.length; j++) {
            diacriticsBaseMap[letters[j]] = diacritics[i].letter;
        }
    }
    // we need to normalize the Unicode form of the file name since some users might have different encoding for special characters
    // e.g certain Unicode characters can be represented in a composed and decomposed form. For example,
    // the German umlaut-u ü can be represented either by the single character ü or by u followed by ¨, resulting in different Unicode encoding.
    fileName = unicodeNormalization(fileName);

    if (umlautRegex.test(fileName)) {
        return fileName.replace(umlautRegex, (letter: string | number) => diacriticsBaseMap[letter]);
    }
    return fileName;
}

/**
 * Function returns the Unicode Normalization Form of the file name provided by user.
 * @param filename name of the file provided by user
 * @returns Unicode Normalization Form of the file name
 */
export function unicodeNormalization(filename: string): string {
    return filename.normalize('NFC');
}

/**
 * Finds all duplicate filenames and stores their name and number of occurrences into an object.
 * @param files - Array of user input files.
 * @returns An object containing filenames and their reoccurrence count.
 */
export function countDuplicateFilenames(files: string[]): { [key: string]: number } {
    const count = {};

    files.forEach(file => {
        count[file] = (count[file] || 0) + 1;
    });
    return count;
}

/**
 * Checks if a file with same name already exists. If file with same name exists add a copy number to it, then allow to upload.
 * @param inputFiles - List of user uploaded filenames.
 * @returns Array of files that are unique based on their name.
 */
export function findAndRenameDuplicateFilenamesWithoutDiacritics(files: UploadFileItem[]): UploadFileItem[] {
    const countStorage: string[] = [];

    files.forEach(file => {
        const fileName = getFileNameWithoutExtension(file.file.name);
        const fileExtension = getFileExtension(file.file.name || '');

        if (countStorage.includes(fileName)) {
            const copyNumber = countDuplicateFilenames(countStorage);
            file.file.name = fileName.concat(`(${copyNumber[fileName]})`, `.${fileExtension}`);
        }
        countStorage.push(fileName);
    });
    return files;
}

export function getUpdatedSelectedFiles(selectedFiles: FileModel[], updatedFiles: FileModel[]): FileModel[] {
    return selectedFiles.map(selectedFile => {
        for (const updatedFileElement of updatedFiles) {
            if (
                (updatedFileElement.originalFileId != null && selectedFile.id === updatedFileElement.originalFileId) ||
                (updatedFileElement.originalFileId == null && selectedFile.id === updatedFileElement.id)
            ) {
                return updatedFileElement;
            }
        }
        return selectedFile; // Return the original file if no match is found
    });
}

export function getConvertedAndNotValidatedFiles(files: FileModel[]): FileModel[] {
    return files.filter(file => file.convertStatus === 'Success' && (file.validationStatus == null || file.validationStatus === 'Unknown'));
}

export function getNumberOfInvalidDatasetFiles(files: FileModel[]): number {
    return files.filter(file => (file.validationStatus ? hasInvalidValidationStatus(file.validationStatus) : false)).length;
}

// Dataset validation logic
export function isConvertedAndHasInvalidValidationStatus(
    convertStatus: CziConversionStatus,
    validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus,
): boolean {
    return convertStatus === 'Success' && hasInvalidValidationStatus(validationStatus);
}

export function isConversionFailedAndHasInvalidValidationStatus(
    convertStatus: CziConversionStatus,
    validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus,
): boolean {
    return hasFailedConversion(convertStatus) && hasInvalidValidationStatus(validationStatus);
}

export function hasInvalidValidationStatus(validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus): boolean {
    return !hasUnknownValidationStatus(validationStatus) && !hasPendingValidation(validationStatus) && hasInvalidValidation(validationStatus);
}

export function hasUnknownValidationStatus(validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus): boolean {
    return validationStatus === 'Unknown';
}

export function hasPendingValidation(validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus): boolean {
    return validationStatus === 'PendingValidation';
}

export function hasFailedConversion(convertStatus: CziConversionStatus | undefined): boolean {
    return convertStatus === undefined || (convertStatus !== 'Unknown' && convertStatus !== 'Active' && convertStatus !== 'Success');
}

export function hasInvalidValidation(validationStatus: FileDatasetValidationStatus | FileSegmentationValidationStatus | undefined): boolean {
    return validationStatus === undefined || (validationStatus !== 'Unknown' && validationStatus !== 'PendingValidation' && validationStatus !== 'Valid');
}

export function containsOnlyValidDatasetFile(files: FileModel[]): boolean {
    return files.filter(file => file.validationStatus === 'Valid').length === files.length;
}

export function containsOnlyConversionFailedFile(files: FileModel[]): number {
    return files.filter(file => file.convertStatus === 'Failed').length;
}

export function containsOnlyValidSegmentationFiles(files: FileModel[]): boolean {
    return files.filter(file => file.convertStatus === 'Success' && file.validationStatus === 'Valid').length === files.length;
}

export function containsPendingCZIConversion(files: FileModel[]): boolean {
    return files.filter(file => file.convertStatus === 'Active').length > 0;
}
